import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CCS2Icon } from '../../../../assets/icons/device-management/ccs2.svg';
import { ReactComponent as DeviceIcon } from '../../../../assets/icons/device-management/device-icon.svg';
import { ReactComponent as ResetIcon } from '../../../../assets/icons/device-management/reset.svg';
import { ReactComponent as RestartIcon } from '../../../../assets/icons/device-management/restart.svg';
import { ReactComponent as StartCharging } from '../../../../assets/icons/device-management/start-charging.svg';
import { ReactComponent as StopCharging } from '../../../../assets/icons/device-management/stop-charging.svg';
import { useAppBranding } from '../../../../config/branding';
import { allStatus, ocppMessageType } from '../../../../config/enum';
import { ApiRequest } from '../../../../interface/common.interface';
import {
  AvailableTabProperties,
  ConnectorProperties,
  DeviceDetailsProperties,
} from '../../../../interface/device-management.interface';
import { useAppSelector } from '../../../../redux/hooks';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../../shared-functions/ConvertUTCtoHHMM';
import FormatUtcString from '../../../../shared-functions/FormatUtcString';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import device from '../../../../utils/api/device';
import deviceMetadataApi from '../../../../utils/api/device-metadata';
import ocppEvents from '../../../../utils/api/ocpp-events';

interface ConnectorMenuProperties {
  connectorId: number;
  isActive: boolean;
}

interface ConnectorHistoryProperties {
  id: string;
  _id: string;
  date: string;
  time: string;
  amount: string;
  powerConsumed: string;
  transactionStartsAt: string;
}

interface DeviceContentTabsProperties {
  selectedDeviceGeneral: DeviceDetailsProperties;
  setSelectedDeviceGeneral: React.Dispatch<React.SetStateAction<DeviceDetailsProperties>>;
}

interface ChargingPointMetadataFilter {
  modelSeries: string;
}

interface MediaInterface {
  url: string;
  fileType: string;
  fileSize: string;
  fileName: string;
}

interface ChargingPointMetadata {
  defaultMedias: MediaInterface[];
}
/**
 * Component to display the tabs of the device content
 * @returns
 */
function DeviceContentTabs(properties: DeviceContentTabsProperties) {
  const branding = useAppBranding();
  const { t } = useTranslation('devices');
  const { images: brandImage } = useAppBranding();
  const { selectedDeviceGeneral, setSelectedDeviceGeneral } = properties;
  const { selectedChargingPoint } = useAppSelector((state) => state.device);
  const [sortOptions, setSortOptions] = useState<Record<string, 'asc' | 'desc'>>({
    date: 'desc',
    time: 'desc',
    amount: 'desc',
    powerConsumed: 'desc',
  });
  const [connectorHistory, setConnectorHistory] = useState<ConnectorHistoryProperties[]>([]);
  const [containerTabs, setContainerTabs] = useState<AvailableTabProperties[]>([
    {
      label: t('general'),
      key: 'general',
      isActive: true,
    },
    {
      label: t('controls'),
      key: 'controls',
      isActive: false,
    },
    {
      label: t('media'),
      key: 'media',
      isActive: false,
    },
    {
      label: t('connectorHistory'),
      key: 'connector-history',
      isActive: false,
    },
  ]);

  const [connectorMenu, setConnectorMenu] = useState<ConnectorMenuProperties[]>([]);
  const [availableConnectors, setAvailableConnectors] = useState<ConnectorProperties[]>([]);

  const [deviceMetadata, setDeviceMetadata] = useState<ChargingPointMetadata>({
    defaultMedias: [],
  });
  const [modalView, setModalView] = useState({
    status: false,
    title: '',
    header: false,
    content: <div />,
    rightCorner: '',
    notification: true,
  });

  const deviceGeneralDetails = [
    {
      id: 1,
      label: t('name'),
      key: 'chargingPointName',
    },
    {
      id: 2,
      label: t('deviceId'),
      key: 'chargingPointSn',
    },
    {
      id: 3,
      label: t('deviceSNo'),
      key: 'chargingPointSn',
    },
    {
      id: 4,
      label: t('stationName'),
      key: 'stationName',
    },
    {
      id: 5,
      label: t('facilityName'),
      key: 'facilityName',
    },
    {
      id: 6,
      label: t('chargePointVendor'),
      key: 'chargePointVendor',
    },
    {
      id: 7,
      label: t('firmwareVersion'),
      key: 'firmwareVersion',
    },
    {
      id: 8,
      label: t('deviceModel'),
      key: 'chargePointModel',
    },
  ];

  /**
   * Modal box close function
   */
  const closeModal = () => {
    setModalView({
      status: false,
      title: '',
      header: false,
      content: <div />,
      rightCorner: '',
      notification: false,
    });
  };

  /**
   * Completed the update task
   * @param message
   */
  const ShowSuccessNotification = (message: string) => {
    setModalView({
      status: true,
      title: '',
      header: false,
      content: (
        <SuccessNotification
          title={message}
          description=""
          notificationCloseTimer={3000}
          onNotificationClose={closeModal}
        />
      ),
      rightCorner: '',
      notification: true,
    });
  };

  /**
   * Reset Device
   * @param type
   */
  const handleResetDevice = async (type: 'Hard' | 'Soft') => {
    try {
      await ocppEvents.anyCommand({
        type: ocppMessageType.RESET_CP,
        body: {
          resetType: type,
        },
        chargingPointSns: [selectedChargingPoint?.chargingPointSn],
      });
      ShowSuccessNotification('Charging point reset successfully!');
    } catch (error) {
      console.log(error);
    }
  };

  const deviceControls = [
    {
      id: 1,
      label: t('restart'),
      svgIcon: <RestartIcon />,
      type: ocppMessageType.RESET_CP,
      /**
       * On-click functionality
       */
      onClickFunc: () => handleResetDevice('Soft'),
    },
    {
      id: 2,
      label: t('reset'),
      svgIcon: <ResetIcon />,
      /**
       * On-click functionality
       */
      onClickFunc: () => handleResetDevice('Hard'),
      type: ocppMessageType.RESET_CP,
    },
  ];

  const column = [
    {
      label: t('date'),
      accessorKey: 'date' as keyof ConnectorHistoryProperties,
      sortAllow: true,
    },
    {
      label: t('time'),
      accessorKey: 'time' as keyof ConnectorHistoryProperties,
      sortAllow: true,
    },
    {
      label: t('amount'),
      accessorKey: 'amount' as keyof ConnectorHistoryProperties,
      sortAllow: true,
      /**
       * Function to show unit with amount
       * @param rowData
       * @returns
       */
      customData: (rowData: ConnectorHistoryProperties) => (
        <p>
          {ToFixedValue(rowData?.amount || 0)} {branding.text.currency}
        </p>
      ),
    },
    {
      label: t('energyConsumed'),
      accessorKey: 'powerConsumed' as keyof ConnectorHistoryProperties,
      sortAllow: true,
      /**
       * Function to show unit with power conception
       * @param rowData
       * @returns
       */
      customData: (rowData: ConnectorHistoryProperties) => (
        <p>{ToFixedValue(Number(rowData?.powerConsumed || 0) / 100)} Kwh</p>
      ),
    },
  ];

  /**
   * Function to switch the tab
   * @param {string} key
   */
  const handleSwitchTab = (key: string) => {
    setContainerTabs((previousTabs: AvailableTabProperties[]) =>
      previousTabs.map((tab) => {
        if (tab.key === key) {
          return {
            ...tab,
            isActive: true,
          };
        }
        return {
          ...tab,
          isActive: false,
        };
      }),
    );
  };

  /**
   * General details
   * @param chargingPointSn
   */
  const handleFetchDeviceGeneralDetails = async (id: string) => {
    try {
      const response = await device.fetchChargingPointGeneralDetails(id);
      setSelectedDeviceGeneral({
        ...response.data,
        stationName: response?.data?.chargingStation?.name || '',
      });
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * Device Metadata
   * @param modelSeries
   */
  const handleFetchDeviceMetadata = async (filter: ChargingPointMetadataFilter) => {
    try {
      const response = await deviceMetadataApi.fetchChargingPointMetadata(filter);
      setDeviceMetadata(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  // To-do -> Need additional inputs for start and stop charging

  /**
   * Connector selection change
   * @param selectIndex
   */
  const handleSelectConnector = (selectIndex: number) => {
    setConnectorMenu((previousValue) =>
      previousValue.map((eachValue, index) => {
        let updatedValue;
        if (index === selectIndex) {
          updatedValue = {
            ...eachValue,
            isActive: true,
          };
        } else {
          updatedValue = {
            ...eachValue,
            isActive: false,
          };
        }
        return updatedValue;
      }),
    );
  };

  /**
   * Handle fetch connector history
   */
  const handleFetchConnectorHistory = async (options?: ApiRequest) => {
    try {
      const parameters: any = {
        chargingPointSn: selectedChargingPoint?.chargingPointSn,
        connector: connectorMenu.findIndex((menu) => menu.isActive) + 1,
      };

      const sortField = options?.sortField || null;

      if (sortField) {
        // Get current sort for this column
        const currentSort = sortOptions[sortField] || 'asc';

        // Determine new sort order
        let sortBy: 'asc' | 'desc' = 'asc';
        if (options?.sortField === sortField) {
          sortBy = currentSort === 'asc' ? 'desc' : 'asc';
        }

        parameters.sortField = sortField;
        parameters.sortBy = sortBy;

        // Update state for that column
        setSortOptions((previous) => ({
          ...previous,
          [sortField]: sortBy,
        }));
      }

      const response = await device.fetchConnectorHistory(parameters);

      setConnectorHistory(
        response.data.map((eachData: ConnectorHistoryProperties) => ({
          ...eachData,
          id: eachData._id,
          date: ConvertUTCtoDate(eachData.transactionStartsAt),
          time: FormatUtcString(ConvertUTCtoHHMM(eachData.transactionStartsAt)),
        })),
      );
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Connector click event
   * @param connectorId
   * @param type
   */
  const handleConnectorClickEvent = async (
    connectorId: string | number,
    type: 'start' | 'stop',
  ) => {
    try {
      if (type === 'start') {
        await ocppEvents.anyCommand({
          type: ocppMessageType.RMT_START_TRANSACTION,
          body: {
            connectorId: Number(connectorId),
          },
          chargingPointSns: [selectedChargingPoint?.chargingPointSn],
        });
        setAvailableConnectors((previous) =>
          previous?.map((connector) =>
            connector.connectorId === Number(connectorId)
              ? {
                  ...connector,
                  status: allStatus.CHARGING,
                }
              : connector,
          ),
        );
        ShowSuccessNotification('Start charging requested');
      } else if (type === 'stop') {
        await ocppEvents.stopTransaction({
          chargingPointSn: selectedChargingPoint?.chargingPointSn,
          connectorId: Number(connectorId),
        });
        setAvailableConnectors((previous) =>
          previous?.map((connector) =>
            connector.connectorId === Number(connectorId)
              ? {
                  ...connector,
                  status: allStatus.FINISHED,
                }
              : connector,
          ),
        );
        ShowSuccessNotification('Stop charging requested');
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (connectorMenu.length) {
      handleFetchConnectorHistory();
    }
  }, [connectorMenu]);

  /**
   * sort Filter function
   * @param value
   */
  const pageNateLimitChange = (value: ApiRequest) => {
    handleFetchConnectorHistory(value);
  };

  useEffect(() => {
    if (selectedChargingPoint) {
      handleFetchDeviceGeneralDetails(selectedChargingPoint.id);

      if (selectedChargingPoint.modelSeries) {
        handleFetchDeviceMetadata({
          modelSeries: selectedChargingPoint.modelSeries,
        });
      }
    }
  }, [selectedChargingPoint]);

  useEffect(() => {
    if (selectedDeviceGeneral.connectors.length) {
      setConnectorMenu(
        Array.from({ length: selectedDeviceGeneral.connectors.length }, (_, index) => ({
          connectorId: index + 1,
          isActive: index === 0, // First object is active
        })),
      );
    }
  }, [selectedDeviceGeneral]);

  useEffect(() => {
    if (selectedDeviceGeneral?.connectors?.length) {
      setAvailableConnectors(selectedDeviceGeneral?.connectors);
    }
  }, [selectedDeviceGeneral]);

  return (
    <div className="device-content-tabs-wrap">
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={closeModal}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />
      <div className="device-tabs">
        {containerTabs.map((tab) => (
          <div
            key={tab.key}
            className={`each-tab ${tab.isActive ? 'active' : ''}`}
            role="presentation"
            onClick={() => handleSwitchTab(tab.key)}>
            <h6>{tab.label}</h6>
            <div className="bottom-green" />
          </div>
        ))}
      </div>
      <div className="device-content">
        {(() => {
          const activeTab = containerTabs.find((tab) => tab.isActive);
          let dataToShow = null;
          if (activeTab) {
            switch (activeTab.key) {
              case 'general':
                dataToShow = (
                  <div className="device-general-details-wrap">
                    <div className="device-general-image-container">
                      <img
                        src={deviceMetadata.defaultMedias?.[0]?.url ?? brandImage.emptyChargerImage}
                        alt=""
                        className="device-details-image"
                      />
                      <div
                        className={`device-status-container ${selectedDeviceGeneral.isDeviceOffline ? t('offline') : t('online')}`}>
                        <div className="static-container" />
                        <div className="animation-container" />
                      </div>
                      <div className="device-status-text-wrap">
                        <div
                          className={`device-status-text-container ${selectedDeviceGeneral.isDeviceOffline ? t('offline') : t('online')}`}>
                          <div className="icon" />
                          <p>
                            {selectedDeviceGeneral.isDeviceOffline ? t('offline') : t('online')}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="device-general-details-container">
                      {deviceGeneralDetails.map((generalDetail) => {
                        let detailToShow = null;
                        if (Object.keys(selectedDeviceGeneral).includes(generalDetail.key)) {
                          detailToShow = (
                            <div className="each-general-detail">
                              <div className="icon-container">
                                <DeviceIcon />
                              </div>
                              <div className="general-detail-content">
                                <h1>
                                  {String(
                                    selectedDeviceGeneral[
                                      generalDetail.key as keyof DeviceDetailsProperties
                                    ],
                                  )}
                                </h1>
                                <h6>{generalDetail.label}</h6>
                              </div>
                            </div>
                          );
                        }
                        return detailToShow;
                      })}
                    </div>
                  </div>
                );
                break;
              case 'controls':
                dataToShow = (
                  <div className="device-controls-wrap">
                    <div className="device-general-image-container">
                      <img
                        src={brandImage.emptyChargerImage}
                        alt=""
                        className="device-details-image"
                      />
                      <div
                        className={`device-status-container ${selectedDeviceGeneral.isDeviceOffline ? t('offline') : t('online')}`}>
                        <div className="static-container" />
                        <div className="animation-container" />
                      </div>
                      <div className="device-status-text-wrap">
                        <div
                          className={`device-status-text-container ${selectedDeviceGeneral.isDeviceOffline ? t('offline') : t('online')}`}>
                          <div className="icon" />
                          <p>
                            {selectedDeviceGeneral.isDeviceOffline ? t('offline') : t('online')}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="device-controls-container">
                      {deviceControls.map((deviceControl) => (
                        <div
                          className="device-control"
                          key={deviceControl.id}
                          role="presentation"
                          onClick={deviceControl.onClickFunc}>
                          <div className="device-control-icon">{deviceControl.svgIcon}</div>
                          <h6>{deviceControl.label}</h6>
                        </div>
                      ))}
                      {availableConnectors.map(
                        (connector) =>
                          (connector?.status === allStatus.CHARGING ||
                            connector?.status === allStatus.PREPARING) && (
                            <div
                              className="device-control"
                              key={connector.connectorId}
                              role="presentation"
                              onClick={() =>
                                handleConnectorClickEvent(
                                  connector.connectorId,
                                  connector?.status === allStatus.CHARGING ? 'stop' : 'start',
                                )
                              }>
                              <div className="device-control-icon">
                                {connector?.status === allStatus.CHARGING && <StopCharging />}
                                {connector?.status === allStatus.PREPARING && <StartCharging />}
                              </div>
                              <h6>
                                {t('connector')} {connector.connectorId}
                              </h6>
                            </div>
                          ),
                      )}
                    </div>
                  </div>
                );
                break;
              case 'media':
                dataToShow = (
                  <div className="device-medias-wrap">
                    <div className="device-medias-container">
                      <img src={brandImage.chargingDevice} alt="" className="device-media" />
                    </div>
                  </div>
                );
                break;
              case 'connector-history':
                dataToShow = (
                  <div className="connector-history-wrap">
                    <div className="connector-menus">
                      {connectorMenu.map((menu, index) => (
                        <div
                          className={`connector-menu ${menu.isActive && 'active-connector'}`}
                          role="presentation"
                          onClick={!menu.isActive ? () => handleSelectConnector(index) : () => {}}>
                          {t('connector')} {index + 1}
                        </div>
                      ))}
                    </div>
                    <div className="connector-history-container">
                      <CustomTable
                        header={column}
                        data={connectorHistory}
                        serialNo
                        filter={{}}
                        filterClearFunction={() => {}}
                        filterFunction={() => {}}
                        actionButton={false}
                        limit={1}
                        page={1}
                        loading={false}
                        totalPages={1}
                        totalResults={1}
                        pageLimitChange={pageNateLimitChange}
                        sizeChangeButtonRequired={false}
                        disablePagination
                        disableHeader
                      />
                    </div>
                  </div>
                );
                break;
              default:
                // dataToShow = <div>General</div>;
                break;
            }
          }
          return dataToShow;
        })()}
      </div>
      <div>
        {selectedDeviceGeneral?.connectors && selectedDeviceGeneral.connectors.length ? (
          <div className="connector-wrap">
            {selectedDeviceGeneral.connectors.map((connector, index) => (
              <div className="each-connector">
                <div className="connector-type">
                  {/* To-do -> Need to configure icon based on connector type */}
                  <span>
                    <CCS2Icon style={{ color: 'var(--primary-color)' }} />
                  </span>
                  <h6>{`(${selectedDeviceGeneral.connectorType})`}</h6>
                </div>
                <p>
                  {t('connector')} {connector.connectorId}
                </p>
                <div className={`connector-status ${connector.status}`}>{connector.status}</div>
                {selectedDeviceGeneral?.connectors &&
                  index !== selectedDeviceGeneral.connectors.length - 1 && (
                    <div className="right-border" />
                  )}
              </div>
            ))}
          </div>
        ) : (
          <h6>{t('connectorDataUnavailable')}</h6>
        )}
      </div>
    </div>
  );
}

export default DeviceContentTabs;
